import type { ProjectDto } from '@/api/ApiClient';

export type ReportingCategoryContextInput = {
  projectIds?: readonly number[] | null;
};

/**
 * Unassigned samples are in-house for reporting category; assigned samples need project type from the API.
 */
export function evaluateReportingCategoryContextSync(input: ReportingCategoryContextInput): boolean | 'needs-fetch' {
  const projectIds = input.projectIds ?? [];

  if (projectIds.length === 0) {
    return true;
  }

  return 'needs-fetch';
}

/** Project ids not present in cached options.projects (auth-filtered getAll list). */
export function projectIdsMissingFromResolver(
  projectIds: readonly number[],
  resolveProject: (id: number) => ProjectDto | undefined
): number[] {
  return projectIds.filter((id) => resolveProject(id) == null);
}

export function resolveProjectsForSample(
  projectIds: readonly number[],
  resolveProject: (id: number) => ProjectDto | undefined,
  fetchedById?: ReadonlyMap<number, ProjectDto>
): ProjectDto[] {
  const projects: ProjectDto[] = [];
  for (const id of projectIds) {
    const project = resolveProject(id) ?? fetchedById?.get(id);
    if (project) {
      projects.push(project);
    }
  }
  return projects;
}

/**
 * When projects are in options (shared cache), resolve by id and use ProjectType.IsInternal.
 */
export function isInHouseReportingCategoryContext(
  projectIds: readonly number[],
  resolveProject: (id: number) => ProjectDto | undefined,
  fetchedById?: ReadonlyMap<number, ProjectDto>
): boolean {
  if (projectIds.length === 0) {
    return true;
  }

  const projects = resolveProjectsForSample(projectIds, resolveProject, fetchedById);
  if (projects.length !== projectIds.length) {
    return false;
  }

  return isInHouseReportingCategoryContextFromProjects(projects);
}

/** After project.get for each id — all must be internal (ProjectType.IsInternal). */
export function isInHouseReportingCategoryContextFromProjects(projects: readonly ProjectDto[]): boolean {
  return projects.length > 0 && projects.every((p) => p.type?.isInternal === true);
}
